import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const WISHeroSection = ({ onDemoRequest, onFeaturesExploration }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [warehouseCount, setWarehouseCount] = useState(247);

  const translations = {
    en: {
      headline: "Master Your Aluminum Inventory with Smart Warehouse Management",
      subheadline: "Achieve 99.9% Inventory Accuracy and Real-Time Material Tracking Across Multiple Locations",
      primaryCTA: "Schedule WIS Demo",
      secondaryCTA: "Explore WIS Features",
      trustMetrics: {
        accuracy: "99.9% Inventory Accuracy",
        tracking: "Real-Time Material Tracking",
        warehouses: "Active Warehouses"
      }
    },
    hi: {
      headline: "स्मार्ट वेयरहाउस प्रबंधन के साथ अपनी एल्यूमीनियम इन्वेंटरी में महारत हासिल करें",
      subheadline: "कई स्थानों पर 99.9% इन्वेंटरी सटीकता और रियल-टाइम मैटेरियल ट्रैकिंग प्राप्त करें",
      primaryCTA: "WIS डेमो शेड्यूल करें",
      secondaryCTA: "WIS सुविधाओं का अन्वेषण करें",
      trustMetrics: {
        accuracy: "99.9% इन्वेंटरी सटीकता",
        tracking: "रियल-टाइम मैटेरियल ट्रैकिंग",
        warehouses: "सक्रिय वेयरहाउस"
      }
    }
  };

  const t = translations[currentLanguage];

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }

    // Simulate real-time warehouse count updates
    const interval = setInterval(() => {
      setWarehouseCount(prev => prev + Math.floor(Math.random() * 2));
    }, 8000);

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-blue-50 via-background to-indigo-50 pt-20">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=%2260%22 height=%2260%22 viewBox=%220 0 60 60%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg fill=%22none%22 fill-rule=%22evenodd%22%3E%3Cg fill=%22%23000000%22 fill-opacity=%220.1%22%3E%3Crect x=%220%22 y=%220%22 width=%2210%22 height=%2210%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content Column */}
          <div className="space-y-8">
            {/* Main Headline */}
            <div className="space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary leading-tight">
                {t.headline}
              </h1>
              
              <p className="text-lg md:text-xl text-text-secondary leading-relaxed max-w-2xl">
                {t.subheadline}
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="primary"
                size="lg"
                onClick={onDemoRequest}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-cta hover-scale"
                iconName="Calendar"
                iconPosition="left"
                iconSize={20}
              >
                {t.primaryCTA}
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                onClick={onFeaturesExploration}
                className="border-primary text-primary hover:bg-primary-50"
                iconName="Package"
                iconPosition="left"
                iconSize={20}
              >
                {t.secondaryCTA}
              </Button>
            </div>

            {/* Trust Metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-8">
              <div className="text-center sm:text-left">
                <div className="flex items-center justify-center sm:justify-start space-x-2 mb-2">
                  <Icon name="Target" size={20} color="var(--color-success)" />
                  <span className="text-2xl font-bold text-primary">99.9%</span>
                </div>
                <p className="text-sm text-text-secondary">{t.trustMetrics.accuracy}</p>
              </div>
              
              <div className="text-center sm:text-left">
                <div className="flex items-center justify-center sm:justify-start space-x-2 mb-2">
                  <Icon name="Activity" size={20} color="var(--color-info)" />
                  <span className="text-2xl font-bold text-primary">Real-Time</span>
                </div>
                <p className="text-sm text-text-secondary">{t.trustMetrics.tracking}</p>
              </div>
              
              <div className="text-center sm:text-left">
                <div className="flex items-center justify-center sm:justify-start space-x-2 mb-2">
                  <Icon name="MapPin" size={20} color="var(--color-primary)" />
                  <span className="text-2xl font-bold text-primary">{warehouseCount}</span>
                </div>
                <p className="text-sm text-text-secondary">{t.trustMetrics.warehouses}</p>
              </div>
            </div>
          </div>

          {/* Visual Column */}
          <div className="relative">
            <div className="relative bg-white rounded-2xl shadow-lg p-8 border border-border">
              {/* Mock WIS Dashboard */}
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-primary">WIS Dashboard</h3>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-success rounded-full animate-pulse"></div>
                    <span className="text-sm text-text-secondary">Live Tracking</span>
                  </div>
                </div>
                
                {/* Mock Inventory Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-text-secondary">Stock Level</span>
                      <Icon name="Package" size={16} color="var(--color-info)" />
                    </div>
                    <div className="text-2xl font-bold text-primary">4,276</div>
                    <div className="text-xs text-success">+5% from last week</div>
                  </div>
                  
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-text-secondary">Accuracy</span>
                      <Icon name="Target" size={16} color="var(--color-success)" />
                    </div>
                    <div className="text-2xl font-bold text-primary">99.9%</div>
                    <div className="text-xs text-success">+0.2% this month</div>
                  </div>
                </div>
                
                {/* Mock Material Tracking */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-text-secondary">Recent Material Movements</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-2 bg-surface-100 rounded">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-success rounded-full"></div>
                        <span className="text-xs">AL-6063-T5-20x40</span>
                      </div>
                      <span className="text-xs text-text-secondary">Zone A2</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-surface-100 rounded">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-info rounded-full"></div>
                        <span className="text-xs">AL-6061-T6-25x25</span>
                      </div>
                      <span className="text-xs text-text-secondary">Zone B1</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-surface-100 rounded">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-warning rounded-full"></div>
                        <span className="text-xs">AL-6082-T6-30x30</span>
                      </div>
                      <span className="text-xs text-text-secondary">Zone C3</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Floating Elements */}
            <div className="absolute -top-4 -right-4 bg-success text-white rounded-full p-3 shadow-lg">
              <Icon name="CheckCircle" size={24} />
            </div>
            
            <div className="absolute -bottom-4 -left-4 bg-info text-white rounded-full p-3 shadow-lg">
              <Icon name="Package" size={24} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WISHeroSection;